import * as XLSX from 'xlsx';
import type { ImportRowInput } from '@/api/journalImport.api';

/**
 * The journal import spreadsheet: its template, and reading one back (GAP-017).
 *
 * Parsing happens here rather than on the server because the upload endpoint's contract is a list
 * of rows, not a file — and that is the right split. A spreadsheet is a transport format; the rules
 * that decide whether any of it is a journal (does the account exist in THIS company, is it a
 * posting account, does each group balance) are the server's, and `validate` applies them. Nothing
 * here judges a row. A row this parser cannot make sense of is reported, never guessed at.
 */

interface ColumnSpec {
  /** The header written into the template, and matched — case- and space-insensitively — on read. */
  header: string;
  /** Alternative spellings accepted on read, so a hand-edited sheet still imports. */
  aliases?: string[];
  width: number;
  note: string;
}

const COLUMNS: ColumnSpec[] = [
  { header: 'Journal',       aliases: ['journal group', 'group', 'voucher'], width: 14,
    note: 'Rows sharing a value become one journal' },
  { header: 'Account Code',  aliases: ['account', 'gl account'],             width: 16,
    note: 'Must exist in this company and be a posting account' },
  { header: 'Cost Centre',   aliases: ['cost center', 'costcentre'],         width: 14,
    note: 'Optional' },
  { header: 'Location',      aliases: ['location code', 'branch'],           width: 14,
    note: 'Optional' },
  { header: 'Document Date', aliases: ['doc date', 'date'],                  width: 14,
    note: 'yyyy-mm-dd' },
  { header: 'Posting Date',  aliases: ['post date'],                         width: 14,
    note: 'yyyy-mm-dd — defaults to the document date' },
  { header: 'Debit',         aliases: ['debit amount', 'dr'],                width: 14,
    note: 'Leave blank or 0 on a credit line' },
  { header: 'Credit',        aliases: ['credit amount', 'cr'],               width: 14,
    note: 'Leave blank or 0 on a debit line' },
  { header: 'Currency',      aliases: ['currency code', 'ccy'],              width: 10,
    note: 'Optional' },
  { header: 'Narration',     aliases: ['description'],                       width: 34,
    note: 'Optional' },
  { header: 'Reference',     aliases: ['ref'],                               width: 18,
    note: 'Optional' },
];

export const normalise = (value: string) => value.trim().toLowerCase().replace(/[\s_]+/g, ' ');

/** header text → the position it occupies in COLUMNS. Built once, used for every sheet read. */
const HEADER_INDEX = new Map<string, number>(
  COLUMNS.flatMap((c, i) =>
    [c.header, ...(c.aliases ?? [])].map(h => [normalise(h), i] as [string, number])),
);

// ── Template ─────────────────────────────────────────────────────────────────

/**
 * Downloads the import template: the header row, a note row, and one worked example.
 *
 * The example is a balanced two-line journal, because the single most common failed import is a
 * sheet where the author did not realise the two sides had to be separate rows sharing a group.
 * Showing it costs one row and saves that round trip.
 */
export function downloadJournalImportTemplate(): void {
  const aoa: unknown[][] = [
    COLUMNS.map(c => c.header),
    COLUMNS.map(c => c.note),
    // Codes and currency that exist in the shipped chart of accounts. The example used to name
    // 5100, which is in no chart this product seeds, so anyone who downloaded the template and
    // filled it in the way the example showed had their import rejected — "Account '5100' does
    // not exist in this company" — on the first row, before they had done anything wrong.
    ['JV-001', '6201', '', '', '2026-08-31', '2026-08-31', 25000, 0, 'USD', 'August office rent', 'INV-8891'],
    ['JV-001', '1110', '', '', '2026-08-31', '2026-08-31', 0, 25000, 'USD', 'August office rent', 'INV-8891'],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = COLUMNS.map(c => ({ wch: c.width }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Journals');
  XLSX.writeFile(wb, 'journal-import-template.xlsx');
}

// ── Reading a sheet back ─────────────────────────────────────────────────────

export interface ParsedSheet {
  rows: ImportRowInput[];
  /** Rows this parser could not read at all. Reported, never silently dropped. */
  problems: { rowNumber: number; message: string }[];
}

/**
 * A cell that should be a date, as yyyy-MM-dd — or null.
 *
 * Excel dates arrive as a serial number when the cell is formatted as a date and as text when it
 * is not, and both are normal in a sheet a person edited. Anything else returns null and the row
 * carries no date, which the server then defaults; guessing at "31/08" without knowing the
 * author's locale would post a journal into the wrong month.
 */
export function readDate(value: unknown): string | null {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  const text = String(value).trim();
  // ISO only. A sheet written by the template is ISO; anything else is ambiguous by locale.
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/** A cell that should be money. Blank, dashes and stray thousands separators all mean a number. */
export function readAmount(value: unknown): number {
  if (value == null || value === '' || value === '-') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value).replace(/[,\s]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export const readText = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length === 0 ? null : text;
};

/**
 * Reads the first worksheet of an .xlsx/.csv into rows the upload endpoint accepts.
 *
 * `rowNumber` is the spreadsheet's own row number — what the operator sees in Excel's gutter — so
 * every message the server sends back about "row 14" points at the line they are looking at.
 */
export async function parseJournalImportFile(file: File): Promise<ParsedSheet> {
  const buffer = await file.arrayBuffer();
  const wb     = XLSX.read(buffer, { type: 'array' });

  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('That file has no worksheets.');

  const grid = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
  if (grid.length === 0) throw new Error('That sheet is empty.');

  // Find the header row rather than assuming row 1: people add a title or a company name above it.
  let headerRow = -1;
  let mapping: (number | undefined)[] = [];

  for (let i = 0; i < Math.min(grid.length, 20); i++) {
    const candidate = (grid[i] ?? []).map(cell => HEADER_INDEX.get(normalise(String(cell ?? ''))));
    // A header row is one that names, at minimum, the three columns without which a line is not a
    // journal line at all.
    const found = new Set(candidate.filter((v): v is number => v !== undefined));
    if (found.has(0) && found.has(1) && (found.has(6) || found.has(7))) {
      headerRow = i;
      mapping   = candidate;
      break;
    }
  }

  if (headerRow === -1)
    throw new Error(
      'Could not find the header row. The sheet needs columns named Journal, Account Code, Debit '
    + 'and Credit — download the template to see the expected layout.');

  const rows: ImportRowInput[] = [];
  const problems: ParsedSheet['problems'] = [];

  const cell = (row: unknown[], column: number): unknown => {
    const at = mapping.indexOf(column);
    return at === -1 ? null : row[at];
  };

  for (let i = headerRow + 1; i < grid.length; i++) {
    const row = grid[i] ?? [];
    const spreadsheetRow = i + 1;   // 1-based, as Excel shows it

    if (row.every(c => c == null || String(c).trim() === '')) continue;

    const journalGroup = readText(cell(row, 0));
    const accountCode  = readText(cell(row, 1));
    const debit        = readAmount(cell(row, 6));
    const credit       = readAmount(cell(row, 7));

    // The note row in the template describes the columns; it is not data. Recognised by having no
    // group and no amounts, which is also true of any other stray line of prose.
    if (!journalGroup && !accountCode && debit === 0 && credit === 0) continue;

    if (!journalGroup) {
      problems.push({ rowNumber: spreadsheetRow,
        message: 'No journal group — this row cannot be attached to a journal.' });
      continue;
    }

    if (debit < 0 || credit < 0) {
      // A negative debit is a credit written the wrong way round. Posting it as-is would balance
      // arithmetically and be wrong on the face of the ledger.
      problems.push({ rowNumber: spreadsheetRow,
        message: 'Negative amount. Put the value on the other side instead of negating it.' });
      continue;
    }

    const documentDate = readDate(cell(row, 4));

    rows.push({
      rowNumber:      spreadsheetRow,
      journalGroup,
      accountCode,
      costCenterCode: readText(cell(row, 2)),
      locationCode:   readText(cell(row, 3)),
      documentDate,
      postingDate:    readDate(cell(row, 5)) ?? documentDate,
      debitAmount:    debit,
      creditAmount:   credit,
      currencyCode:   readText(cell(row, 8))?.toUpperCase() ?? null,
      narration:      readText(cell(row, 9)),
      reference:      readText(cell(row, 10)),
    });
  }

  if (rows.length === 0)
    throw new Error(
      problems.length > 0
        ? 'No usable rows — every row below the header had a problem. See the list below.'
        : 'No rows found below the header row.');

  return { rows, problems };
}
